/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { PaperSheet } from '../../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../../shared/ticket/TicketKpiStrip';
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { formatTurnOpenDurationCompact } from '../cashManagementUtils';
import type { CashVm } from './cashManagement.types';
import { formatMoneyCompact } from '../../../shared/format/moneyFormat';
import { cashArqueoCopy, cashNonCashTodayLabel } from './cashArqueoCopy';

type Props = {
  vm: CashVm;
};

/** Fila KPI compartida (record header desktop + mobile) — Square soft tiles. */
export function CashTurnoKpiRow({ vm }: Props) {
  const { layoutWebShell } = useContentLayout('dataWide');
  const items = useMemo((): TicketKpiItem[] => {
    const activeSession = vm.cashSummary?.activeSession;
    const expected = vm.expectedCashInDrawer;
    const sessionSalesTotal = vm.cashSummary?.sessionSalesTotal ?? 0;
    const sessionSalesCount = vm.cashSummary?.sessionSalesCount ?? 0;
    const durationCompact = activeSession?.openedAt
      ? formatTurnOpenDurationCompact(activeSession.openedAt)
      : null;

    if (activeSession) {
      // Turno abierto: Efectivo esperado + Duración siempre; Monto/Ventas solo
      // cuando hay ventas (turno recién abierto = tiles en 0 que no informan).
      const hasSales = sessionSalesCount > 0 || sessionSalesTotal > 0;
      const cells: Array<TicketKpiItem | null> = [
        expected != null
          ? { label: cashArqueoCopy.theoreticalBalance, value: formatMoneyCompact(expected) }
          : { label: cashArqueoCopy.theoreticalBalance, value: '—' },
        hasSales
          ? { label: cashArqueoCopy.sessionSalesAmountLabel, value: formatMoneyCompact(sessionSalesTotal) }
          : null,
        hasSales
          ? { label: cashArqueoCopy.sessionSalesCountLabel, value: String(sessionSalesCount) }
          : null,
        durationCompact
          ? { label: 'Duración', value: durationCompact }
          : { label: 'Duración', value: '—' },
      ];
      return cells.filter((cell): cell is TicketKpiItem => cell != null);
    }

    // Turno cerrado: 4 tiles (Ventas hoy · Monto hoy · Efectivo hoy · No efectivo hoy).
    // Sin movimiento hoy → strip vacío: cuatro ceros no informan nada.
    const todayCount = vm.cashSummary?.todaySalesCount ?? 0;
    const todayTotal = vm.cashSummary?.todaySalesTotal ?? 0;
    const todayCash = vm.cashSummary?.todayCashTotal ?? 0;
    const todayDigital = vm.cashSummary?.todayDigitalTotal ?? 0;
    if (todayCount === 0 && todayTotal === 0 && todayCash === 0 && todayDigital === 0) {
      return [];
    }
    const nonCashLabel = cashNonCashTodayLabel(vm.customerModuleEnabled);
    return [
      { label: 'Ventas hoy', value: String(todayCount) },
      { label: 'Monto hoy', value: formatMoneyCompact(todayTotal) },
      { label: 'Efectivo hoy', value: formatMoneyCompact(todayCash) },
      { label: nonCashLabel, value: formatMoneyCompact(todayDigital) },
    ];
  }, [vm]);

  if (items.length === 0) return null;

  return (
    <PaperSheet testID="caja.kpi.sheet">
      <TicketKpiStrip
        items={items}
        layout={layoutWebShell ? 'desk' : 'mobile'}
        variant="soft"
      />
    </PaperSheet>
  );
}
